/**
 * Production Showcase Component
 * Carousel with slide navigation, interactive hotspot tooltips, touch/swipe support
 */
(function () {
  'use strict';

  function ProductionShowcase(container) {
    this.container = container;
    this.slides = container.querySelectorAll('[data-showcase-slide]');
    this.dots = container.querySelectorAll('[data-showcase-dot]');
    this.prevBtn = container.querySelector('[data-showcase-prev]');
    this.nextBtn = container.querySelector('[data-showcase-next]');
    this.hotspots = container.querySelectorAll('[data-hotspot]');

    if (this.slides.length === 0) return;

    this.currentIndex = 1;
    this.autoplay = container.dataset.autoplay === 'true';
    this.autoplaySpeed = parseInt(container.dataset.autoplaySpeed, 10) || 5000;
    this.autoplayTimer = null;
    this.isMobile = 'ontouchstart' in window;

    this.init();
  }

  ProductionShowcase.prototype.init = function () {
    this.bindCarouselEvents();
    this.bindHotspotEvents();
    this.positionTooltips();

    if (this.autoplay && this.slides.length > 1) {
      this.startAutoplay();
    }

    var self = this;
    window.addEventListener('resize', debounce(function () {
      self.positionTooltips();
    }, 200));
  };

  /* ------------------------------------------------
     Carousel Navigation
     ------------------------------------------------ */

  ProductionShowcase.prototype.bindCarouselEvents = function () {
    var self = this;

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', function () { self.prev(); });
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', function () { self.next(); });
    }

    this.dots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        var index = parseInt(dot.dataset.showcaseDot, 10);
        self.goTo(index);
      });
    });

    // Pause autoplay on hover
    if (this.autoplay) {
      this.container.addEventListener('mouseenter', function () { self.stopAutoplay(); });
      this.container.addEventListener('mouseleave', function () { self.startAutoplay(); });
    }

    // Touch/swipe
    var touchStartX = 0;
    var touchEndX = 0;

    this.container.addEventListener('touchstart', function (e) {
      touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });

    this.container.addEventListener('touchend', function (e) {
      touchEndX = e.changedTouches[0].screenX;
      var diff = touchStartX - touchEndX;
      if (Math.abs(diff) > 50) {
        if (diff > 0) { self.next(); }
        else { self.prev(); }
      }
    }, { passive: true });

    // Keyboard navigation
    this.container.setAttribute('tabindex', '0');
    this.container.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        self.prev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        self.next();
      }
    });
  };

  ProductionShowcase.prototype.goTo = function (index) {
    if (index === this.currentIndex) return;
    if (index < 1 || index > this.slides.length) return;

    this.closeAllTooltips();

    // Deactivate current slide
    var currentSlide = this.container.querySelector('[data-showcase-slide="' + this.currentIndex + '"]');
    if (currentSlide) currentSlide.classList.remove('is-active');

    // Activate target slide
    var targetSlide = this.container.querySelector('[data-showcase-slide="' + index + '"]');
    if (targetSlide) targetSlide.classList.add('is-active');

    // Update dots
    this.dots.forEach(function (dot) {
      var dotIndex = parseInt(dot.dataset.showcaseDot, 10);
      dot.classList.toggle('is-active', dotIndex === index);
      dot.setAttribute('aria-selected', dotIndex === index ? 'true' : 'false');
    });

    this.currentIndex = index;

    // Reposition tooltips for new slide
    var self = this;
    requestAnimationFrame(function () { self.positionTooltips(); });

    // Reset autoplay
    if (this.autoplay) {
      this.stopAutoplay();
      this.startAutoplay();
    }
  };

  ProductionShowcase.prototype.next = function () {
    var slideCount = this.slides.length;
    var nextIndex = (this.currentIndex % slideCount) + 1;
    this.goTo(nextIndex);
  };

  ProductionShowcase.prototype.prev = function () {
    var slideCount = this.slides.length;
    var prevIndex = ((this.currentIndex - 2 + slideCount) % slideCount) + 1;
    this.goTo(prevIndex);
  };

  ProductionShowcase.prototype.startAutoplay = function () {
    if (this.autoplayTimer) return;
    var self = this;
    this.autoplayTimer = setInterval(function () { self.next(); }, this.autoplaySpeed);
  };

  ProductionShowcase.prototype.stopAutoplay = function () {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  };

  /* ------------------------------------------------
     Hotspot Tooltip Behavior
     ------------------------------------------------ */

  ProductionShowcase.prototype.bindHotspotEvents = function () {
    var self = this;

    this.hotspots.forEach(function (hotspot) {
      // Mobile: toggle on click
      if (self.isMobile) {
        hotspot.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          var isVisible = hotspot.classList.contains('is-tooltip-visible');
          self.closeAllTooltips();
          if (!isVisible) {
            hotspot.classList.add('is-tooltip-visible');
          }
        });
      }
    });

    // Close tooltips when clicking outside
    document.addEventListener('click', function (e) {
      if (!e.target.closest('[data-hotspot]')) {
        self.closeAllTooltips();
      }
    });

    // Close tooltips on Escape
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        self.closeAllTooltips();
      }
    });
  };

  ProductionShowcase.prototype.closeAllTooltips = function () {
    this.hotspots.forEach(function (hotspot) {
      hotspot.classList.remove('is-tooltip-visible');
    });
  };

  /* ------------------------------------------------
     Tooltip Viewport Collision Detection
     ------------------------------------------------ */

  ProductionShowcase.prototype.positionTooltips = function () {
    var activeSlide = this.container.querySelector('[data-showcase-slide="' + this.currentIndex + '"]');
    if (!activeSlide) return;

    var imageWrapper = activeSlide.querySelector('.production-showcase__image-wrapper');
    if (!imageWrapper) return;

    var activeHotspots = activeSlide.querySelectorAll('[data-hotspot]');
    var wrapperRect = imageWrapper.getBoundingClientRect();

    activeHotspots.forEach(function (hotspot) {
      var tooltip = hotspot.querySelector('[data-tooltip]');
      if (!tooltip) return;

      // Reset positioning classes
      hotspot.classList.remove('tooltip-below', 'tooltip-shift-left', 'tooltip-shift-right');

      // Temporarily make tooltip visible for measurement
      var prevOpacity = tooltip.style.opacity;
      var prevVisibility = tooltip.style.visibility;
      tooltip.style.opacity = '0';
      tooltip.style.visibility = 'visible';
      tooltip.style.display = 'block';

      var hotspotRect = hotspot.getBoundingClientRect();
      var tooltipRect = tooltip.getBoundingClientRect();
      var viewportWidth = window.innerWidth;

      // Restore
      tooltip.style.opacity = prevOpacity;
      tooltip.style.visibility = prevVisibility;
      tooltip.style.display = '';

      // Flip below if too close to top of image
      if (hotspotRect.top - wrapperRect.top < tooltipRect.height + 20) {
        hotspot.classList.add('tooltip-below');
      }

      // Shift right if too close to left edge
      if (hotspotRect.left - (tooltipRect.width / 2) < 8) {
        hotspot.classList.add('tooltip-shift-right');
      }

      // Shift left if too close to right edge
      if (hotspotRect.right + (tooltipRect.width / 2) > viewportWidth - 8) {
        hotspot.classList.add('tooltip-shift-left');
      }
    });
  };

  /* ------------------------------------------------
     Utility
     ------------------------------------------------ */

  function debounce(fn, delay) {
    var timer;
    return function () {
      clearTimeout(timer);
      timer = setTimeout(fn, delay);
    };
  }

  /* ------------------------------------------------
     Initialization
     ------------------------------------------------ */

  function initShowcases() {
    var containers = document.querySelectorAll('[data-production-showcase]');
    containers.forEach(function (container) {
      if (!container.dataset.initialized) {
        var instance = new ProductionShowcase(container);
        container._productionShowcase = instance;
        container.dataset.initialized = 'true';
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initShowcases);
  } else {
    initShowcases();
  }

  // Shopify theme editor support
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (event) {
      var container = event.target.querySelector('[data-production-showcase]');
      if (container) {
        container.dataset.initialized = '';
        var instance = new ProductionShowcase(container);
        container._productionShowcase = instance;
        container.dataset.initialized = 'true';
      }
    });

    // Navigate to correct slide when a block is selected in the editor
    document.addEventListener('shopify:block:select', function (event) {
      var block = event.target;
      var container = block.closest('[data-production-showcase]');
      if (!container) return;

      var instance = container._productionShowcase;
      if (!instance) return;

      // If it's a slide block, navigate to it
      var slideAttr = block.getAttribute('data-showcase-slide');
      if (slideAttr) {
        instance.goTo(parseInt(slideAttr, 10));
        return;
      }

      // If it's a hotspot block, find its parent slide and navigate + show tooltip
      if (block.hasAttribute('data-hotspot') || block.querySelector('[data-hotspot]')) {
        var hotspotEl = block.hasAttribute('data-hotspot') ? block : block.querySelector('[data-hotspot]');
        var parentSlide = hotspotEl.closest('[data-showcase-slide]');
        if (parentSlide) {
          var slideIndex = parseInt(parentSlide.getAttribute('data-showcase-slide'), 10);
          instance.goTo(slideIndex);
          setTimeout(function () {
            instance.closeAllTooltips();
            hotspotEl.classList.add('is-tooltip-visible');
          }, 100);
        }
      }
    });
  }
})();
